import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  StarBorder,
  Star,
  AddCircleOutline,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import { productAPI, activityLogAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { ViewPageHeader, ActivityPanel, ConfirmDialog } from '../../components/common';

const initialFormData = {
  name: '',
  sku: '',
  description: '',
  productType: 'goods',
  canBeSold: true,
  canBePurchased: true,
  canBeRented: false,
  isFavorite: false,
  category: 'hardware',
  unitPrice: 0,
  costPrice: 0,
  taxRate: 18,
  salesTaxes: ['18% GST'],
  purchaseTaxes: ['18% GST'],
  reference: '',
  barcode: '',
  hsnCode: '',
  sacCode: '',
  trackInventory: false,
  invoicingPolicy: 'ordered',
  company: 'Visible to all',
  internalNotes: '',
};

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getById(id);
      setFormData({
        ...initialFormData,
        ...response.data,
      });
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const dataToSave = {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice) || 0,
        costPrice: parseFloat(formData.costPrice) || 0,
        taxRate: parseFloat(formData.taxRate) || 0,
      };
      
      // Remove empty string fields that should be null/undefined
      if (!dataToSave.sku || dataToSave.sku.trim() === '') {
        delete dataToSave.sku;
      }
      if (!dataToSave.reference || dataToSave.reference.trim() === '') {
        delete dataToSave.reference;
      }
      if (!dataToSave.barcode || dataToSave.barcode.trim() === '') {
        delete dataToSave.barcode;
      }
      if (!dataToSave.hsnCode || dataToSave.hsnCode.trim() === '') {
        delete dataToSave.hsnCode;
      }
      if (!dataToSave.sacCode || dataToSave.sacCode.trim() === '') {
        delete dataToSave.sacCode;
      }
      
      if (isEdit) {
        await productAPI.update(id, dataToSave);
        toast.success('Product updated successfully');
      } else {
        const response = await productAPI.create(dataToSave);
        toast.success('Product created successfully');
        navigate(`/products/${response.data.product._id}`);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFavorite = () => {
    setFormData({ ...formData, isFavorite: !formData.isFavorite });
  };

  const handleDuplicate = async () => {
    if (!isEdit) return;
    try {
      setDuplicating(true);
      const response = await productAPI.duplicate(id);
      const newProduct = response.data.product;
      toast.success(`Product duplicated as "${newProduct.name}"`);
      navigate(`/products/${newProduct._id}`);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.message || 'Failed to duplicate product');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await productAPI.delete(id);
      toast.success('Product deleted successfully');
      navigate('/products');
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleRemoveTax = (type, tax) => {
    if (type === 'sales') {
      setFormData({
        ...formData,
        salesTaxes: formData.salesTaxes.filter(t => t !== tax),
      });
    } else {
      setFormData({
        ...formData,
        purchaseTaxes: formData.purchaseTaxes.filter(t => t !== tax),
      });
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <ViewPageHeader
        parentTitle="Products"
        currentTitle={isEdit ? formData.name || 'Edit Product' : 'New Product'}
        onParentClick={() => navigate('/products')}
        onNewClick={() => {
          setFormData(initialFormData);
          navigate('/products/new');
        }}
        onClose={() => navigate('/products')}
        showNewButton
        additionalActions={
          isEdit && (
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Tooltip title="Duplicate product">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                  onClick={handleDuplicate}
                  disabled={duplicating}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#dee2e6',
                    color: '#333',
                    fontWeight: 400,
                    fontSize: '13px',
                    height: 30,
                    px: 1.5,
                    '&:hover': { borderColor: '#875A7B' },
                  }}
                >
                  {duplicating ? 'Duplicating...' : 'Duplicate'}
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete sx={{ fontSize: 16 }} />}
                onClick={handleDelete}
                sx={{
                  textTransform: 'none',
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  fontWeight: 400,
                  fontSize: '13px',
                  height: 30,
                  px: 1.5,
                  '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                }}
              >
                Delete
              </Button>
            </Box>
          )
        }
      />

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, p: 2 }}>
        {/* Left - Form */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <Box sx={{ pb: 3, borderBottom: '1px solid #e0e0e0' }}>
              {/* Top Section - Product Name and Image */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                {/* Image Upload Section */}
                <Box
                  sx={{
                    width: 110,
                    height: 110,
                    border: '2px dashed #dee2e6',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { borderColor: '#875A7B' },
                  }}
                >
                  <AddCircleOutline sx={{ fontSize: 40, color: '#adb5bd' }} />
                </Box>

                {/* Right Side - Name and Checkboxes */}
                <Box sx={{ flex: 1 }}>
                  {/* Product label */}
                  <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>Product</Typography>
                
                {/* Name Field with Star */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <IconButton size="small" onClick={handleToggleFavorite} sx={{ p: 0 }}>
                    {formData.isFavorite ? (
                      <Star sx={{ fontSize: 24, color: '#f5c518' }} />
                    ) : (
                      <StarBorder sx={{ fontSize: 24, color: '#999' }} />
                    )}
                  </IconButton>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Cheese Burger"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: '24px',
                        fontWeight: 400,
                        color: '#495057',
                        '&::placeholder': { color: '#adb5bd' },
                      },
                    }}
                    sx={{ borderBottom: '1px solid #dee2e6' }}
                  />
                </Box>

                {/* Checkboxes */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.canBeSold}
                        onChange={handleChange}
                        name="canBeSold"
                        size="small"
                        sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: '13px' }}>Sales</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.canBePurchased}
                        onChange={handleChange}
                        name="canBePurchased"
                        size="small"
                        sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: '13px' }}>Purchase</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.canBeRented}
                        onChange={handleChange}
                        name="canBeRented"
                        size="small"
                        sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '13px' }}>
                        Rental <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>

            {/* Tabs Section */}
            <Box sx={{ mt: 4 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2,
                    color: '#666',
                    fontSize: '13px',
                  },
                  '& .Mui-selected': {
                    color: '#212529 !important',
                    fontWeight: 500,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#875A7B',
                  },
                }}
              >
                <Tab label="General Information" />
                <Tab label="Sales" />
                <Tab label="Purchase" />
                <Tab label="Inventory" />
              </Tabs>

              {/* Tab Content */}
              <Box sx={{ py: 2 }}>
                {activeTab === 0 && (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Product Type */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#495057' }}>Product Type</Typography>
                          <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Box>
                        <RadioGroup
                          row
                          name="productType"
                          value={formData.productType}
                          onChange={handleChange}
                        >
                          <FormControlLabel
                            value="goods"
                            control={<Radio size="small" sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }} />}
                            label={<Typography sx={{ fontSize: '13px' }}>Goods</Typography>}
                          />
                          <FormControlLabel
                            value="service"
                            control={<Radio size="small" sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }} />}
                            label={<Typography sx={{ fontSize: '13px' }}>Service</Typography>}
                          />
                          <FormControlLabel
                            value="combo"
                            control={<Radio size="small" sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }} />}
                            label={<Typography sx={{ fontSize: '13px' }}>Combo</Typography>}
                          />
                        </RadioGroup>
                      </Box>

                      {/* Track Inventory */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#495057' }}>Track Inventory</Typography>
                          <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Box>
                        <Checkbox
                          checked={formData.trackInventory}
                          onChange={handleChange}
                          name="trackInventory"
                          size="small"
                          sx={{ ml: -1, color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                        />
                      </Box>

                      {/* Invoicing Policy */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#495057' }}>Invoicing Policy</Typography>
                          <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>
                          {formData.invoicingPolicy === 'ordered' ? 'Ordered quantities' : 'Delivered quantities'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Sales Price */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                          Sales Price <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Typography>
                        <TextField
                          name="unitPrice"
                          value={formData.unitPrice}
                          onChange={handleChange}
                          variant="standard"
                          size="small"
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5, fontSize: '13px' }}>₹</Typography>,
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 100, borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Sales Taxes */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                          Sales Taxes <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Typography>
                        <TextField
                          type="number"
                          value={parseFloat(String(formData.salesTaxes?.[0] || formData.taxRate || 0)) || 0}
                          onChange={(e) => {
                            const v = e.target.value;
                            const n = parseFloat(v);
                            const clean = Number.isFinite(n) ? n : 0;
                            setFormData({
                              ...formData,
                              salesTaxes: [`${clean}% GST`],
                              taxRate: clean,
                            });
                          }}
                          variant="standard"
                          size="small"
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: <Typography sx={{ ml: 0.5, fontSize: '13px', color: '#495057' }}>% GST</Typography>,
                            inputProps: { min: 0, max: 100, step: 0.5 },
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 120, borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Cost */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                          Cost <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Typography>
                        <TextField
                          name="costPrice"
                          value={formData.costPrice}
                          onChange={handleChange}
                          variant="standard"
                          size="small"
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5, fontSize: '13px' }}>₹</Typography>,
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 100, borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Purchase Taxes */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                          Purchase Taxes <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Typography>
                        <TextField
                          type="number"
                          value={parseFloat(String(formData.purchaseTaxes?.[0] || formData.taxRate || 0)) || 0}
                          onChange={(e) => {
                            const v = e.target.value;
                            const n = parseFloat(v);
                            const clean = Number.isFinite(n) ? n : 0;
                            setFormData({
                              ...formData,
                              purchaseTaxes: [`${clean}% GST`],
                            });
                          }}
                          variant="standard"
                          size="small"
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: <Typography sx={{ ml: 0.5, fontSize: '13px', color: '#495057' }}>% GST</Typography>,
                            inputProps: { min: 0, max: 100, step: 0.5 },
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ width: 120, borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Category */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Category</Typography>
                        <TextField
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Reference */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Reference</Typography>
                        <TextField
                          name="reference"
                          value={formData.reference}
                          onChange={handleChange}
                          placeholder="Enter reference..."
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Barcode */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Barcode</Typography>
                        <TextField
                          name="barcode"
                          value={formData.barcode}
                          onChange={handleChange}
                          placeholder="Enter barcode..."
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* HSN/SAC Code */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                          HSN/SAC Code <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                        </Typography>
                        <TextField
                          name="hsnCode"
                          value={formData.hsnCode || formData.sacCode}
                          onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value, sacCode: e.target.value })}
                          placeholder="Enter code..."
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '13px' },
                          }}
                          sx={{ borderBottom: '1px solid #dee2e6' }}
                        />
                      </Box>

                      {/* Company */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Company</Typography>
                        <Typography sx={{ fontSize: '13px', color: '#017e84' }}>Visible to all</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Typography sx={{ color: '#666', fontSize: '13px' }}>Sales tab content - Coming soon</Typography>
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Typography sx={{ color: '#666', fontSize: '13px' }}>Purchase tab content - Coming soon</Typography>
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box>
                    <Typography sx={{ color: '#666', fontSize: '13px' }}>Inventory tab content - Coming soon</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Internal Notes */}
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #dee2e6' }}>
              <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                INTERNAL NOTES
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                placeholder="This note is only for internal purposes."
                variant="outlined"
                size="small"
              />
            </Box>

            {/* Save Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving || !formData.name}
                sx={{
                  backgroundColor: '#875A7B',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { backgroundColor: '#714B68' },
                  '&:disabled': { backgroundColor: '#d0d0d0', color: '#888' },
                }}
              >
                {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : isEdit ? 'Save' : 'Create Product'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/products')}
                sx={{
                  borderColor: '#ccc',
                  color: '#666',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#999' },
                }}
              >
                Discard
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Right - Activity Panel */}
        <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
          {isEdit && id ? (
            <ActivityPanel
              documentType="product"
              documentId={id}
              documentNumber={formData.name}
              user={user}
              fetchActivities={async (docType, docId) => {
                try {
                  const response = await activityLogAPI.getByDocument(docType, docId);
                  return response.data;
                } catch (error) {
                  console.error('Error fetching product activities:', error);
                  return { logs: [] };
                }
              }}
              onSendMessage={async (data) => {
                try {
                  await activityLogAPI.addMessage('product', id, data.content, formData.name);
                  toast.success('Message sent');
                } catch (error) {
                  toast.error('Failed to send message');
                }
              }}
              onLogNote={async (data) => {
                try {
                  await activityLogAPI.addNote('product', id, data.content, formData.name);
                  toast.success('Note logged');
                } catch (error) {
                  toast.error('Failed to log note');
                }
              }}
              onScheduleActivity={(data) => toast.info('Activity scheduled')}
              showFollowers
              followers={[]}
            />
          ) : (
            <ActivityPanel
              activities={[]}
              user={user}
              onSendMessage={(data) => toast.info('Save the product first to send messages')}
              onLogNote={(data) => toast.info('Save the product first to log notes')}
              onScheduleActivity={(data) => toast.info('Save the product first to schedule activities')}
              showFollowers
              followers={[]}
            />
          )}
        </Box>
      </Box>

      {/* Delete Confirmation */}
      {isEdit && (
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          title="Delete Product"
          message={`Are you sure you want to delete "${formData.name}"? This action cannot be undone.`}
          type="danger"
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default ProductForm;
